import React from 'react';
import { McTag } from '@maersk-global/mds-react-wrapper/components-core/mc-tag';
import { COLORS, BORDER_RADIUS } from '../../constants/theme';

// Compact read-only view of extracted JSON, built from MDS typography, table and tag styles:
// objects render as a labelled form, long text as paragraphs, arrays as tables.
// Everything uses the MDS x-small text size so dense extraction results stay scannable.
const LABEL_CLASS = 'mds-text--x-small-bold';
const VALUE_CLASS = 'mds-text--x-small-normal';

// MDS's smallest table uses the "small" text size; scope its variables to x-small and tighter cells.
const typo = (size, prop) => `var(--mds_brand_typography_text_${size}_${prop})`;
const COMPACT_TABLE_VARS = {
  '--mds_brand_typography_text_small_desktop_font-size': typo('x-small', 'desktop_font-size'),
  '--mds_brand_typography_text_small_desktop_line-height': typo('x-small', 'desktop_line-height'),
  '--mds_brand_typography_text_small_mobile_font-size': typo('x-small', 'mobile_font-size'),
  '--mds_brand_typography_text_small_mobile_line-height': typo('x-small', 'mobile_line-height'),
  '--mds_foundations_table_small_cell_padding-top': '2px',
  '--mds_foundations_table_small_cell_padding-bottom': '2px',
  '--mds_foundations_table_small_cell_padding-left': '8px',
  '--mds_foundations_table_small_cell_padding-right': '8px',
};

const LONG_TEXT_LENGTH = 80;

export const humanizeKey = (key) => String(key)
  .replace(/[_-]+/g, ' ')
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .trim()
  .replace(/^\w/, (c) => c.toUpperCase());

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const isEmptyValue = (v) => v === null || v === undefined || (typeof v === 'string' && v.trim() === '');
const isLongText = (v) => typeof v === 'string' && (v.length > LONG_TEXT_LENGTH || v.includes('\n'));

const Empty = () => (
  <span style={{ color: COLORS.textSecondary }} aria-label="No value">—</span>
);

// A single scalar value; long or multi-line strings become paragraphs.
const ScalarValue = ({ value }) => {
  if (isEmptyValue(value)) return <Empty />;
  if (typeof value === 'boolean') return <>{value ? 'Yes' : 'No'}</>;
  if (typeof value !== 'string') return <>{String(value)}</>;
  if (!isLongText(value)) return <>{value}</>;
  return (
    <div className="mds-flex mds-flex-column mds-gap-100">
      {value.split(/\n\s*\n/).map((paragraph, i) => (
        <p key={i} style={{ margin: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{paragraph}</p>
      ))}
    </div>
  );
};

const ItemCount = ({ count }) => (
  <McTag fit="small" appearance="neutral-weak" label={`${count} ${count === 1 ? 'item' : 'items'}`} />
);

const SectionTitle = ({ title, count }) => (
  <div className="mds-flex mds-flex-items-center mds-gap-100" style={{ marginBottom: 4 }}>
    <span className={LABEL_CLASS} style={{ color: COLORS.text }}>{title}</span>
    {count !== undefined && <ItemCount count={count} />}
  </div>
);

// Array → table. Objects get one column per key (in first-seen order); scalars get a single "Value" column.
const ArrayTable = ({ items, compact = false }) => {
  if (items.length === 0) return <Empty />;
  const objectRows = items.every((item) => isPlainObject(item) || isEmptyValue(item));
  const columns = objectRows
    ? [...new Set(items.flatMap((item) => (isPlainObject(item) ? Object.keys(item) : [])))]
    : ['value'];
  const cellOf = (item, col) => (objectRows ? (isPlainObject(item) ? item[col] : undefined) : item);
  // Columns holding only numbers (or blanks) are right-aligned, as MDS tables do for numeric data.
  const numeric = new Set(columns.filter((col) => {
    const values = items.map((item) => cellOf(item, col)).filter((v) => !isEmptyValue(v));
    return values.length > 0 && values.every((v) => typeof v === 'number');
  }));

  return (
    <div className="mds-table mds-table--small mds-table--scrollable mds-table--disable-row-highlight-on-hover" style={COMPACT_TABLE_VARS}>
      <table>
        <thead>
          <tr>
            <th className={`mds-table__cell--number ${LABEL_CLASS}`} style={{ width: 28 }}>#</th>
            {columns.map((col) => (
              <th key={col} className={`${LABEL_CLASS}${numeric.has(col) ? ' mds-table__cell--number' : ''}`}>
                {objectRows ? humanizeKey(col) : 'Value'}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item, rowIndex) => (
            <tr key={rowIndex}>
              <td className={`mds-table__cell--number ${VALUE_CLASS}`} style={{ color: COLORS.textSecondary }}>{rowIndex + 1}</td>
              {columns.map((col) => {
                const cell = cellOf(item, col);
                return (
                  <td
                    key={col}
                    className={`${VALUE_CLASS}${numeric.has(col) ? ' mds-table__cell--number' : ''}`}
                    style={{ verticalAlign: 'top', minWidth: isLongText(cell) ? 200 : undefined }}
                  >
                    <AnyValue value={cell} compact />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Object → read-only form: label/value rows, long text full width, nested objects and arrays as sections.
const ObjectForm = ({ data, compact = false }) => {
  const entries = Object.entries(data);
  if (entries.length === 0) return <Empty />;
  const rows = entries.filter(([, v]) => !isPlainObject(v) && !Array.isArray(v));
  const sections = entries.filter(([, v]) => isPlainObject(v) || Array.isArray(v));
  return (
    <div className="mds-flex mds-flex-column" style={{ gap: compact ? 4 : 8 }}>
      {rows.length > 0 && (
        <dl style={{ margin: 0 }}>
          {rows.map(([key, value], i) => {
            const last = i === rows.length - 1;
            return (
              <div
                key={key}
                style={{
                  display: 'grid',
                  gridTemplateColumns: compact ? '1fr' : 'minmax(90px, 28%) 1fr',
                  gap: compact ? 0 : 12,
                  padding: compact ? '1px 0' : '3px 0',
                  borderBottom: compact || last ? 'none' : `1px solid ${COLORS.borderLight}`,
                }}
              >
                <dt className={LABEL_CLASS} style={{ color: COLORS.textSecondary, overflowWrap: 'anywhere' }}>{humanizeKey(key)}</dt>
                <dd className={VALUE_CLASS} style={{ margin: 0, color: COLORS.text, overflowWrap: 'anywhere' }}>
                  <ScalarValue value={value} />
                </dd>
              </div>
            );
          })}
        </dl>
      )}
      {sections.map(([key, value]) => (
        <section
          key={key}
          style={compact ? undefined : {
            border: `1px solid ${COLORS.borderLight}`,
            borderRadius: BORDER_RADIUS.md,
            padding: '6px 10px',
            backgroundColor: COLORS.white,
          }}
        >
          {compact
            ? <div className={LABEL_CLASS} style={{ color: COLORS.textSecondary }}>{humanizeKey(key)}</div>
            : <SectionTitle title={humanizeKey(key)} count={Array.isArray(value) ? value.length : undefined} />}
          <AnyValue value={value} compact={compact} />
        </section>
      ))}
    </div>
  );
};

const AnyValue = ({ value, compact = false }) => {
  if (Array.isArray(value)) return <ArrayTable items={value} compact={compact} />;
  if (isPlainObject(value)) return <ObjectForm data={value} compact={compact} />;
  return <ScalarValue value={value} />;
};

const JsonDisplay = ({ data }) => {
  if (data === undefined) return null;
  return (
    <div data-testid="json-display" style={{ color: COLORS.text }}>
      <AnyValue value={data} />
    </div>
  );
};

export default JsonDisplay;
